import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { describe } from '../settings/errors';
import { Button, Icon } from '../ui';
import { exportReport } from './api';
import type { ReportFrame } from './frame';

/**
 * Export CSV, in the page header beside the view switch.
 *
 * It lives up there rather than in the period row because it is an action on the report, not
 * a part of choosing the period — and because the period row now has to hold a custom range
 * without wrapping.
 *
 * The download goes through the authenticated client and arrives as a blob. A plain link
 * carries no bearer token and quietly returns a 401, which reaches whoever asked for the file
 * as a spreadsheet full of JSON.
 *
 * @param path The report endpoint for whatever is on screen — the driver page exports that
 *   driver, the grid exports the grid.
 * @param query What else narrows the screen, as `&name=value` pairs — the call archive's
 *   filters. Empty for every report that has only a period.
 */
export function ExportButton({
  frame,
  path,
  query = '',
}: {
  frame: ReportFrame;
  path: string;
  query?: string;
}) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);

  return (
    <Button
      disabled={busy || frame.busy}
      onClick={() => {
        setBusy(true);
        frame.fail(null);

        void exportReport(path, frame.period, query)
          .catch((error: unknown) => frame.fail(describe(error, t, 'settings.couldNotLoad')))
          .finally(() => setBusy(false));
      }}
    >
      <Icon name="download" small />
      {t('reports.export')}
    </Button>
  );
}
